"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import type { TierOptionalBenefit } from "@/stores/visitor-quotes"

const LABELS: Record<string, string> = {
  dental: "Dental",
  optical: "Optical",
  maternity: "Maternity",
  physiotherapy: "Physiotherapy",
  wellness: "Wellness",
  psychiatric: "Psychiatric",
  alt_medicine: "Alternative Medicine",
  alternative_medicine: "Alternative Medicine",
  health_checkup: "Health Checkup",
  aml: "Annual Limit",
  pec: "Pre-existing and Chronic Conditions",
  room_type: "Room Type",
  // micro_sme_auh — migrated from adjustment_factors
  consultation_deductible: "Consultations Deductible",
  op_diagnostic_copay: "OP Diagnostic Copay",
  pharmacy_copay: "Pharmacy Co pay",
  pharmacy_limit: "Pharmacy Limit",
  ip_copay: "Copayment on IP",
  territory: "Territorial Cover",
}

interface Props {
  benefitType: string
  options: TierOptionalBenefit[]
  selectedBenefitId: string | null
  onSelectOption: (benefitId: string) => void
}

export function OptionalBenefitToggle({
  benefitType,
  options,
  selectedBenefitId,
  onSelectOption,
}: Props) {
  const label = LABELS[benefitType] ?? benefitType
  const selected = options.find((o) => o.id === selectedBenefitId) ?? null

  const copayValues = React.useMemo(() => {
    const set = new Set<number>()
    for (const o of options) if (o.copayPct != null) set.add(o.copayPct)
    return Array.from(set).sort((a, b) => a - b)
  }, [options])

  const limitValues = React.useMemo(() => {
    const set = new Set<string>()
    for (const o of options) if (o.limitValue) set.add(o.limitValue.trim())
    return Array.from(set)
  }, [options])

  const hasCopayAndLimit = copayValues.length > 1 && limitValues.length > 1

  const currentCopay = selected?.copayPct ?? null
  const hasNullCopayOption = options.some(o => o.copayPct === null)
  
  const filteredLimits = React.useMemo(() => {
    if (currentCopay === null) return []
    const seen = new Set<string>()
    const out: { limit: string; id: string; rate: number }[] = []
    for (const o of options) {
      if (o.copayPct !== currentCopay) continue
      const lim = o.limitValue ? o.limitValue.trim() : "default"
      if (seen.has(lim)) continue
      seen.add(lim)
      out.push({ limit: lim, id: o.id, rate: o.rate })
    }
    return out
  }, [options, currentCopay])

  function handleCopayChange(val: string) {
    if (val === "") return
    if (val === "null") {
      const match = options.find((o) => o.copayPct === null)
      if (match) onSelectOption(match.id)
      return
    }
    const copay = Number(val)
    const matching = options.filter((o) => o.copayPct === copay)
    if (matching.length === 0) return
    onSelectOption(matching[0].id)
  }

  function handleLimitChange(limitVal: string) {
    const match = options.find(
      (o) =>
        o.copayPct === currentCopay && (o.limitValue ? o.limitValue.trim() : "default") === limitVal
    )
    if (match) onSelectOption(match.id)
  }

  return (
    <div
      className={cn(
        "rounded-xl border p-4 transition-colors",
        "border-nova-300 bg-nova-50 dark:border-nova-600 dark:bg-nova-900/20"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-nova-700 dark:text-nova-300">
          {label}
        </p>
      </div>

      {hasCopayAndLimit && (
        <div className="mt-3 space-y-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">
              {currentCopay === null && selected ? "Select option" : "Co-pay"}
            </label>
            <select
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-nova-400 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-white"
              value={currentCopay === null && selected ? "null" : (currentCopay ?? "")}
              onChange={(e) => handleCopayChange(e.target.value)}
            >
              <option value="">Select co-pay…</option>
              {copayValues.map((c) => (
                <option key={c} value={c}>
                  {c === 0 ? "Nil (0%)" : `${c}%`}
                </option>
              ))}
              {hasNullCopayOption && (
                <option value="null">Not Covered</option>
              )}
            </select>
          </div>
          {currentCopay !== null && filteredLimits.length > 1 && (
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">
                Limit
              </label>
              <select
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-nova-400 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                value={selected?.limitValue?.trim() ?? ""}
                onChange={(e) => handleLimitChange(e.target.value)}
              >
                <option value="">Select limit…</option>
                {filteredLimits.map((l) => (
                  <option key={l.limit} value={l.limit}>
                    {/* {l.limit} — Rate: {l.rate.toFixed(2)} */}
                    {l.limit}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {!hasCopayAndLimit && options.length > 0 && (
        <div className="mt-3">
          <label className="mb-1 block text-xs font-medium text-slate-500">
            Select option
          </label>
          <select
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-nova-400 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            value={selectedBenefitId ?? ""}
            onChange={(e) => onSelectOption(e.target.value)}
          >
            {options.map((opt) => {
              const limitValue = opt.limitValue?.trim()
              const parts: string[] = []
              if (
                opt.copayPct != null &&
                !(benefitType === "psychiatric" && limitValue === "Not Covered")
              ) {
                parts.push(`${opt.copayPct}% copay`)
              }
              if (limitValue) parts.push(limitValue)
              // parts.push(`Rate: ${opt.rate.toFixed(2)}`)
              return (
                <option key={opt.id} value={opt.id}>
                  {parts.join(" — ")}
                </option>
              )
            })}
          </select>
        </div>
      )}

    </div>
  )
}
